import { Component, OnInit } from '@angular/core';
import { ContactsService } from '../../shared/services/contacts.service';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import {
  AddTagAction,
  RemoveTagAction,
  RevertEditedFilterChanges,
  UpdateTempPageWithItemsAction
} from '../../contacts.actions';
import {
  getEditedFilterItemsSelector,
  getEditedFiltersSelector,
  getOpenedFilterItemSelector,
  getTempEditedFiltersSelector,
  getTempPageSelector
} from '../../contacts.reducer';
import { Filter, FilterItem, FiltersEditedItems, FilterTag, Page } from '../../shared/models/Contact';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FilterService } from '../../shared/services/filter.service';
import { AsYouType } from 'libphonenumber-js';

@Component({
  selector: 'cc-filter-universal',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 1,
          top: 0,
          right: 0
        })
      ),
      state(
        'closed',
        style({
          opacity: 1,
          top: 0,
          right: '-100%'
        })
      ),
      transition('open => closed', [animate('1s ease')]),
      transition('closed => open', [animate('0.5s')])
    ])
  ],
  templateUrl: './filter-universal.component.html',
  styleUrls: ['./filter-universal.component.scss']
})
export class FilterUniversalComponent implements OnInit {
  private editedItemsList: FiltersEditedItems;
  public universalFiltersOpened: boolean;
  public filtersOpened: boolean;
  public filteredItems: FilterItem[];
  public filter: string;
  public currentTempFilters: Filter;
  public indeterminate: boolean;
  public allChecked: boolean;
  public checkboxDisabled: false;
  public isFilteringReady: boolean;
  public editedFiltersList: string[];
  public tempEditedFiltersList: string[];
  public name: string;
  public formatter = new AsYouType();
  public noValidateNumber: boolean;

  constructor(
    public contactsService: ContactsService,
    private store: Store<IState>,
    public filterService: FilterService
  ) {}

  public itemList: FilterItem[];

  ngOnInit() {
    this.contactsService.toggleOpeningFilters.subscribe((opener: boolean) => {
      this.filtersOpened = opener;
    });

    this.store.select(getOpenedFilterItemSelector).subscribe((filter: string) => {
      this.filter = filter;
    });

    this.store.select(getEditedFilterItemsSelector).subscribe((response: FiltersEditedItems) => {
      this.editedItemsList = response;
      if (this.editedItemsList && this.filter) {
        this.itemList = !!this.editedItemsList[this.filter] ? this.editedItemsList[this.filter] : [];
        const filtered = this.itemList.filter(item => item.isFilter === true);
        this.setCheckboxAll(filtered, this.itemList);

        this.assignItemListCopy();
      }
    });

    this.store.select(getEditedFiltersSelector).subscribe((editedFiltersList: string[]) => {
      this.editedFiltersList = editedFiltersList;
    });

    this.store.select(getTempEditedFiltersSelector).subscribe((tempEditedFiltersList: string[]) => {
      this.tempEditedFiltersList = tempEditedFiltersList;
    });

    this.store.select(getTempPageSelector).subscribe((page: Page) => {
      if (page) {
        this.currentTempFilters = page.filter;
      }
    });

    this.contactsService.onTagFilterOff.subscribe((tag: FilterTag) => {
      if (tag && this.editedItemsList[tag.source]) {
        const itemToRemove: FilterItem = {
          name: tag.name,
          type: tag.source,
          isFilter: true,
          projectId: tag.projectId ? tag.projectId : null
        };

        this.toggleFilteringByItem(false, itemToRemove, tag.source);
      }
    });

    this.contactsService.toggleOpeningUniversalFilters.subscribe(opener => {
      this.universalFiltersOpened = opener;

      if (opener) {
        this.isFilteringReady = false;
        this.filterService.getFilterSnapshot();
      }
    });
  }

  checkAllItem(event: any) {
    this.itemList.map(item => {
      this.toggleFilteringByItem(!!event.checked, item);
    });
  }

  toggleFilteringByItem(event: boolean, filterItem: FilterItem, openedFilter = this.filter) {
    let filterItemsListToRequest =
      this.currentTempFilters && this.currentTempFilters[openedFilter] && this.currentTempFilters[openedFilter][0]
        ? this.currentTempFilters[openedFilter]
        : [];

    const itemProp = 'name';

    const listOfFilterItems = this.editedItemsList[openedFilter].map(item => {
      if (item[itemProp] === filterItem[itemProp]) {
        item.isFilter = event;
        const newTag: FilterTag = {
          name: item.name,
          source: openedFilter,
          title: this.contactsService.getFilterTitle(openedFilter)
        };

        if (event) {
          if (!filterItemsListToRequest.includes(filterItem[itemProp])) {
            filterItemsListToRequest.push(item[itemProp]);
          }
          this.store.dispatch(new AddTagAction(newTag));
        } else {
          if (filterItemsListToRequest.includes(filterItem[itemProp])) {
            const newFilterItemsListToRequest = filterItemsListToRequest.filter(el => el !== filterItem[itemProp]);
            filterItemsListToRequest = newFilterItemsListToRequest;
            this.store.dispatch(new RemoveTagAction(newTag));
          }
        }
      }

      return item;
    });

    this.filterService.updateStoreFilterData(
      filterItemsListToRequest,
      listOfFilterItems,
      this.tempEditedFiltersList,
      this.filter
    );
    this.isFilteringReady = true;
    this.setCheckboxAll(listOfFilterItems, filterItemsListToRequest);
  }

  setCheckboxAll(itemsList, requestedProjectList) {
    if (!requestedProjectList.length) {
      this.allChecked = false;
      this.indeterminate = false;
    } else if (requestedProjectList.length === itemsList.length) {
      this.allChecked = true;
      this.indeterminate = false;
    } else if (!!requestedProjectList.length && !itemsList.length) {
      this.allChecked = false;
      this.indeterminate = false;
    } else {
      this.indeterminate = true;
    }
  }

  backToFilters() {
    this.store.dispatch(new RevertEditedFilterChanges());
    this.contactsService.showSearchFilters();
    this.contactsService.hideUniversalFilters();
  }

  filtersResign() {
    this.contactsService.closeAllFilters();
  }

  universalFiltersDone() {
    this.contactsService.showSearchFilters();
    this.contactsService.hideUniversalFilters();
    this.contactsService.onDoneFilter();
    this.store.dispatch(new UpdateTempPageWithItemsAction(this.filter));
  }

  assignItemListCopy() {
    this.filteredItems = [...this.itemList];
  }

  filterItem(value) {
    if (!value) {
      this.assignItemListCopy();
    }

    if (this.filter === 'callerIds') {
      const REGEXP = /^[0-9]*$/;
      const cleanPhrase = value.toLowerCase().replace(/\+|\s|\-|\(|\)/g, '');
      const match = REGEXP.test(cleanPhrase);
      this.noValidateNumber = !match;
      this.filteredItems = this.getFilteredList(this.filteredItems, cleanPhrase);
    } else {
      this.filteredItems = this.getFilteredList(this.filteredItems, value.toLowerCase());
    }
  }

  getFilteredList(itemList, filteredItem) {
    return [...itemList].filter(item => item.name.toLowerCase().indexOf(filteredItem) > -1);
  }

  translateFilterName(filter: string) {
    return this.contactsService.getFilterTitle(filter);
  }

  exitFilter($event) {
    if ($event.target.className === 'nav-text' && this.filtersOpened && this.universalFiltersOpened) {
      this.contactsService.closeAllFilters();
    }
  }

  getCountryCode(number: string, fallbackCountryCode?: string) {
    const asYouType = new AsYouType();
    asYouType.input(number);

    if (!asYouType.country) {
      return fallbackCountryCode && fallbackCountryCode.toLocaleLowerCase();
    }
    return asYouType.country.toLowerCase();
  }
}
